import type { ChatMessage, ChatRole, ChatRoleService, LecturaUser } from '../core/types';
import { ensureFirestoreDb, type FirestoreServiceOptions } from './firestore-service';

interface CreateFirestoreChatRoleServiceOptions extends FirestoreServiceOptions {
  getCurrentUser: () => LecturaUser | null;
  getPresId: () => string;
}

interface RoleState {
  presenterEmails: Set<string>;
  isAdmin: boolean;
  canManagePresentation: boolean;
  role: ChatRole;
}

interface FirestoreDocSnap {
  exists: boolean;
  data(): Record<string, unknown>;
}

interface FirestoreDocRef {
  get(): Promise<FirestoreDocSnap>;
  onSnapshot?(cb: (snap: FirestoreDocSnap) => void, errCb?: (err: unknown) => void): () => void;
}

interface FirestoreDb {
  collection(path: string): {
    doc(id: string): FirestoreDocRef;
  };
}

function normalizeEmail(value: unknown): string {
  // Keep exact casing to match Firestore/Storage security rules comparisons.
  return String(value || '').trim().toLowerCase();
}

function extractPresenterEmails(docData: Record<string, unknown> | null): string[] {
  if (!docData) return [];
  const explicit = Array.isArray(docData.presenters) ? docData.presenters : [];
  const fallback = Array.isArray(docData.authors) ? docData.authors : [];
  const source = explicit.length ? explicit : fallback;
  return source
    .map((v) => normalizeEmail(v))
    .filter((email) => email.includes('@'));
}

export function createFirestoreChatRoleService(opts: CreateFirestoreChatRoleServiceOptions): ChatRoleService {
  let state: RoleState = {
    presenterEmails: new Set<string>(),
    isAdmin: false,
    canManagePresentation: false,
    role: 'viewer'
  };
  let lastKey = '';
  let inflight: Promise<void> | null = null;
  const listeners = new Set<() => void>();
  let liveKey = '';
  let stopAdminWatch: (() => void) | null = null;
  let stopPresWatch: (() => void) | null = null;
  let currentAdminActive = false;
  let currentPresData: Record<string, unknown> | null = null;

  function sameEmailSet(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const value of a) {
      if (!b.has(value)) return false;
    }
    return true;
  }

  function setState(next: RoleState): void {
    const unchanged =
      state.role === next.role &&
      state.isAdmin === next.isAdmin &&
      state.canManagePresentation === next.canManagePresentation &&
      sameEmailSet(state.presenterEmails, next.presenterEmails);
    state = next;
    if (!unchanged) notify();
  }

  function resetState(): void {
    setState({
      presenterEmails: new Set<string>(),
      isAdmin: false,
      canManagePresentation: false,
      role: 'viewer'
    });
  }

  function stopLiveWatchers(): void {
    if (stopAdminWatch) {
      stopAdminWatch();
      stopAdminWatch = null;
    }
    if (stopPresWatch) {
      stopPresWatch();
      stopPresWatch = null;
    }
    liveKey = '';
  }

  function applyRole(_user: LecturaUser, email: string): void {
    const presenterEmails = new Set(extractPresenterEmails(currentPresData));
    const canManagePresentation = currentAdminActive || presenterEmails.has(email);
    const role: ChatRole = currentAdminActive ? 'admin' : canManagePresentation ? 'presenter' : 'viewer';

    setState({
      presenterEmails,
      isAdmin: currentAdminActive,
      canManagePresentation,
      role
    });
  }

  function watchRoleDocs(db: FirestoreDb, user: LecturaUser, presId: string, email: string): void {
    const key = `${email}|${presId}`;
    const sameScope = liveKey === key;
    if (!sameScope) {
      stopLiveWatchers();
      currentAdminActive = false;
      currentPresData = null;
      liveKey = key;
    }
    const adminRef = db.collection('admins').doc(email);
    const presRef = db.collection('presentations').doc(String(presId));

    const loadInitial = Promise.all([adminRef.get().catch(() => null), presRef.get().catch(() => null)]);
    void loadInitial.then(([adminDoc, presDoc]) => {
      // Ignore stale async completion after account/deck switches.
      if (liveKey !== key) return;
      currentAdminActive = !!(adminDoc && adminDoc.exists && adminDoc.data()?.active === true);
      currentPresData = presDoc && presDoc.exists ? presDoc.data() : null;
      applyRole(user, email);
    });

    if (!sameScope && typeof adminRef.onSnapshot === 'function') {
      stopAdminWatch = adminRef.onSnapshot(
        (snap) => {
          if (liveKey !== key) return;
          currentAdminActive = !!(snap && snap.exists && snap.data()?.active === true);
          applyRole(user, email);
        },
        () => {
          // Treat admin watch failure as non-admin; role is recomputed from remaining data.
          if (liveKey !== key) return;
          currentAdminActive = false;
          applyRole(user, email);
        }
      );
    }
    if (!sameScope && typeof presRef.onSnapshot === 'function') {
      stopPresWatch = presRef.onSnapshot(
        (snap) => {
          if (liveKey !== key) return;
          currentPresData = snap && snap.exists ? snap.data() : null;
          applyRole(user, email);
        },
        (err) => {
          if (liveKey !== key) return;
          const code = String((err as { code?: unknown } | null)?.code || '');
          if (code === 'permission-denied') {
            // Immediate demotion path for private decks when presenter/admin access is revoked.
            currentPresData = null;
            applyRole(user, email);
          }
        }
      );
    }
  }

  function notify(): void {
    listeners.forEach((cb) => cb());
  }

  function canDeleteMessage(msg: ChatMessage): boolean {
    const user = opts.getCurrentUser();
    if (!user) return false;
    const ownerId = msg.userId || msg.uid;
    if (user.uid && ownerId && user.uid === ownerId) return true;
    if (state.canManagePresentation) return true;
    const myEmail = normalizeEmail(user.email);
    if (myEmail && state.presenterEmails.has(myEmail)) return true;
    return false;
  }

  function getRole(): ChatRole {
    return state.role;
  }

  function getAuthorRole(msg: ChatMessage): 'admin' | 'presenter' | null {
    if (msg.authorRole === 'admin' || msg.authorRole === 'presenter') return msg.authorRole;
    const currentUser = opts.getCurrentUser();
    const ownerId = msg.userId || msg.uid;
    const normalizedMsgEmail =
      normalizeEmail(msg.authorEmail) ||
      (String(msg.displayName || '').includes('@') ? normalizeEmail(msg.displayName) : '');
    const normalizedCurrentEmail = normalizeEmail(currentUser?.email);

    // Best-effort Admin identification:
    // - reliably for the current signed-in author when they are admin
    if (state.isAdmin && currentUser?.uid && ownerId && currentUser.uid === ownerId) return 'admin';
    if (state.isAdmin && normalizedCurrentEmail && normalizedMsgEmail === normalizedCurrentEmail) return 'admin';

    // Presenter indication for explicit presentation presenters.
    if (normalizedMsgEmail && state.presenterEmails.has(normalizedMsgEmail)) return 'presenter';
    if (state.role === 'presenter' && currentUser?.uid && ownerId && currentUser.uid === ownerId) return 'presenter';
    return null;
  }

  function refresh(): Promise<void> {
    const user = opts.getCurrentUser();
    const presId = opts.getPresId();
    const email = normalizeEmail(user?.email);
    const key = `${email}|${presId}`;
    if (inflight && key === lastKey) return inflight;
    lastKey = key;

    if (!user || user.isAnonymous || !email || !presId) {
      stopLiveWatchers();
      resetState();
      return Promise.resolve();
    }

    inflight = ensureFirestoreDb(opts)
      .then(async (db) => {
        const firestore = db as unknown as FirestoreDb;
        watchRoleDocs(firestore, user, presId, email);
      })
      .catch(() => {
        stopLiveWatchers();
        resetState();
      })
      .finally(() => {
        inflight = null;
      });

    return inflight;
  }

  return {
    canDeleteMessage,
    getRole,
    getAuthorRole,
    refresh,
    subscribe(cb: () => void): () => void {
      listeners.add(cb);
      return () => listeners.delete(cb);
    }
  };
}
